import uniqBy from 'lodash/uniqBy'

import { IPagination } from '@shared/models/proto/base/pagination'
import { Optional } from '@shared/models/types/primary'

export const addToExistingData = <T>(
  responseData: T[],
  storeData?: T[]
): T[] => {
  return [...(storeData ?? []), ...(responseData ?? [])]
}

export const getDataToSet = <T>(
  page: number,
  responseData: T[],
  storeData?: T[]
): T[] => {
  const isLoadingFirstPage = page === 1

  return isLoadingFirstPage
    ? responseData
    : addToExistingData(responseData, storeData)
}

export const getUniqDataToSet = <T>(
  page: number,
  responseData: T[],
  fieldName: string,
  storeData?: T[]
): T[] => {
  const dataToSet = getDataToSet<T>(page, responseData, storeData)

  return uniqBy(dataToSet, fieldName)
}

export const setUpdatedDataToExistingArray = <T>(
  storeData: T[],
  responseData: T[],
  fieldName: keyof T
): T[] => {
  // Create a map of responseData objects keyed by the value of the specified field
  const responseDataMap = new Map(
    responseData.map((obj) => [obj[fieldName], obj])
  )

  // Update the common objects in storeData with values from responseData
  return storeData.map((obj) => {
    const fieldValue = obj[fieldName]
    const responseDataObj = responseDataMap.get(fieldValue)
    if (responseDataObj) {
      // Merge responseDataObj into obj
      return { ...obj, ...responseDataObj }
    }
    return obj
  })
}

export const setNewItemToTheLastPage = <T>(
  item: T,
  storeData: Optional<T[]>,
  storeMeta: Optional<IPagination>
): T[] => {
  if (!storeMeta || !storeData) {
    return [item]
  }
  const canAddToTheLastPage = storeMeta.totalCount % storeMeta.perPage

  if (canAddToTheLastPage) {
    return [...storeData, item]
  }

  return storeData
}

export const updateItemInArray = <T>(
  array: T[],
  updatedItem: T,
  updateByFieldName: keyof T
): T[] => {
  return (
    array.map((item) =>
      item[updateByFieldName] === updatedItem[updateByFieldName]
        ? { ...updatedItem }
        : item
    ) ?? []
  )
}
